import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { isDemoMode } from '../../utils/demoMode'

/**
 * Protected Route Component
 * Allows authenticated customers (role = "customer" or "web_customer")
 * Redirects unauthenticated users to home page
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading, role, isWebCustomer } = useAuth()

  // Debug logging
  console.log('🔒 ProtectedRoute Check:', {
    user: !!user,
    loading,
    role,
    isWebCustomer,
    userEmail: user?.email
  })

  // Campaign demo: the demo user may browse the dashboard (data is local only)
  if (isDemoMode()) {
    return children
  }

  // If we have user and customer role, bypass loading check
  const isCustomer = role === 'customer' || role === 'web_customer' || isWebCustomer
  if (user && isCustomer) {
    console.log('✅ Bypassing loading check - user is valid, granting access')
    return children
  }

  // Show loading state while checking authentication
  if (loading) {
    console.log('⏳ Loading state is TRUE - waiting for auth to complete...')
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-gray-600">Loading your dashboard...</p>
          <p className="text-xs text-gray-500 mt-2">This should take just a moment</p>
        </div>
      </div>
    )
  }

  // User not authenticated - redirect to home
  if (!user) {
    console.log('❌ No user - redirecting to home')
    return <Navigate to="/" replace />
  }

  // User authenticated but not a customer - redirect to home
  if (!isCustomer) {
    console.warn('⚠️ Invalid role for protected route:', role)
    return <Navigate to="/" replace />
  }

  console.log('✅ Access granted to protected route')
  // User is authenticated and has customer role - render children
  return children
}

export default ProtectedRoute
